// 页面静态配置
import React from 'react';
import { rendTitle } from '@sg/utils/commonPageMethod';
import {
  mathFormat,
  toMillions,
  formatPercentValue,
  formatPercentValue100,
  buildPeriod,
  buildFullYearPeriod,
  getYearPrefix,
  getMonthYearPrefix,
  formatVSValue,
} from '@sg/utils/common';
import { IntlShape } from 'react-intl';
import { localizeLabel } from '@sg/utils/translate';
import CommonBubble from '@/pages/home/components/CommonBubble';
import DefinitionBubble from '@sg/components/DefinitionBubble';
import ExpandCollapseButton from "@sg/components/ExpandCollapseButton";
import ProjectCell from "@sg/components/ProjectCell";

export const DefaultChat = {
  icon: "discount-chart",
  value: "bar",
  tip: "Bar chart",
  format: true,
};

export const OverallGrossRevenueColums = (formatMessage: IntlShape['formatMessage']) => {
  const NUMERIC_COLUMN_WIDTH = 170;
  const RIGHT_ALIGN_CELL_STYLE = {
    textAlign: 'right' as const,
    textWrap: 'nowrap' as const,
  };
  const LAST_COLUMN_CELL_STYLE = {
    ...RIGHT_ALIGN_CELL_STYLE,
    paddingRight: '20px',
  };

  return [
    {
      header: formatMessage({ id: 'dbs_month' }),
      dataIndex: 'month',
      key: 'month',
      width: 100,
    },
    {
      header: <div style={RIGHT_ALIGN_CELL_STYLE}>{formatMessage({ id: 'dbs_monthly_total' })}</div>,
      title: formatMessage({ id: 'dbs_monthly_total' }), // Plain text for download
      dataIndex: 'total',
      key: 'total',
      width: 100,
      format: mathFormat,
      render: (item: any) => <div style={RIGHT_ALIGN_CELL_STYLE}>
        {mathFormat(item.total)}
      </div>
    },
    {
      header: <div style={RIGHT_ALIGN_CELL_STYLE}>{formatMessage({ id: 'dbs_monthly_studio_gross_revenue' })}</div>,
      title: formatMessage({ id: 'dbs_monthly_studio_gross_revenue' }), // Plain text for download
      dataIndex: 'studio',
      key: 'studio',
      width: NUMERIC_COLUMN_WIDTH,
      format: mathFormat,
      render: (item: any) => <div style={RIGHT_ALIGN_CELL_STYLE}>
        {mathFormat(item?.studio)}
      </div>
    },
    {
      header: <div style={RIGHT_ALIGN_CELL_STYLE}>{formatMessage({ id: 'dbs_monthly_publishing_gross_revenue' })}</div>,
      title: formatMessage({ id: 'dbs_monthly_publishing_gross_revenue' }), // Plain text for download
      dataIndex: 'publishing',
      key: 'publishing',
      width: NUMERIC_COLUMN_WIDTH,
      format: mathFormat,
      render: (item: any) => <div style={RIGHT_ALIGN_CELL_STYLE}>
        {mathFormat(item?.publishing)}
      </div>
    },
    {
      header: <div style={RIGHT_ALIGN_CELL_STYLE}>{formatMessage({ id: 'dbs_monthly_casual_gross_revenue' })}</div>,
      title: formatMessage({ id: 'dbs_monthly_casual_gross_revenue' }), // Plain text for download
      dataIndex: 'casual',
      key: 'casual',
      width: NUMERIC_COLUMN_WIDTH,
      format: mathFormat,
      render: (item: any) => <div style={RIGHT_ALIGN_CELL_STYLE}>
        {mathFormat(item?.casual)}
      </div>
    },
    {
      header: <div style={LAST_COLUMN_CELL_STYLE}>{formatMessage({ id: 'dbs_monthly_gross_revenue_kpi' })}</div>,
      title: formatMessage({ id: 'dbs_monthly_gross_revenue_kpi' }), // Plain text for download
      dataIndex: 'kpi',
      key: 'kpi',
      width: NUMERIC_COLUMN_WIDTH,
      format: mathFormat,
      render: (item: any) => <div style={LAST_COLUMN_CELL_STYLE}>
        {mathFormat(item?.kpi)}
      </div>
    }
  ];
}

export const OverallProfitColums = (formatMessage: IntlShape['formatMessage']) => [
  {
    header: formatMessage({ id: 'dbs_month' }),
    dataIndex: 'month',
    key: 'month',
    width: 100,
  },
  {
    header: <div style={{ textAlign: 'right', width: `${'Monthly Total Profit'.length * 8}px`, textWrap: 'nowrap' }}>{formatMessage({ id: 'dbs_monthly_total_profit' })}</div>,
    title: formatMessage({ id: 'dbs_monthly_total_profit' }), // Plain text for download
    dataIndex: 'total',
    key: 'total',
    width: 100,
    format: mathFormat,
    render: (item: any) => <div style={{ textAlign: 'right', width: `${'Monthly Total Profit'.length * 8}px`, textWrap: 'nowrap' }}>
      {mathFormat(item?.total)}
    </div>
  },
  {
    header: <div style={{ textAlign: 'right', width: `${'Monthly Studio Profit'.length * 8}px`, textWrap: 'nowrap' }}>{formatMessage({ id: 'dbs_monthly_studio_profit' })}</div>,
    title: formatMessage({ id: 'dbs_monthly_studio_profit' }), // Plain text for download
    dataIndex: 'studio',
    key: 'studio',
    width: 100,
    format: mathFormat,
    render: (item: any) => <div style={{ textAlign: 'right', width: `${'Monthly Studio Profit '.length * 8}px`, textWrap: 'nowrap' }}>
      {mathFormat(item?.studio)}
    </div>
  },
  {
    header: <div style={{ textAlign: 'right', width: `${'Monthly Publishing Profit'.length * 8}px`, textWrap: 'nowrap' }}>{formatMessage({ id: 'dbs_monthly_publishing_profit' })}</div>,
    title: formatMessage({ id: 'dbs_monthly_publishing_profit' }), // Plain text for download
    dataIndex: 'publishing',
    key: 'publishing',
    width: 100,
    format: mathFormat,
    render: (item: any) => <div style={{ textAlign: 'right', width: `${'Monthly Publishing Profit'.length * 8}px`, textWrap: 'nowrap' }}>
      {mathFormat(item?.publishing)}
    </div>
  },
  {
    header: <div style={{ textAlign: 'right', width: `${'Monthly Casual Profit'.length * 8}px`, textWrap: 'nowrap' }}>{formatMessage({ id: 'dbs_monthly_casual_profit' })}</div>,
    title: formatMessage({ id: 'dbs_monthly_casual_profit' }), // Plain text for download
    dataIndex: 'casual',
    key: 'casual',
    width: 100,
    format: mathFormat,
    render: (item: any) => <div style={{ textAlign: 'right', width: `${'Monthly Casual Profit'.length * 8}px`, textWrap: 'nowrap' }}>
      {mathFormat(item?.casual)}
    </div>
  },
  // {
  //   header: <div style={{ textAlign: 'right', width: `${'Monthly Gross Profit KPI'.length * 8}px`, textWrap: 'nowrap' }}>{formatMessage({ id: 'dbs_monthly_gross_profit_kpi' })}</div>,
  //   title: formatMessage({ id: 'dbs_monthly_gross_profit_kpi' }), // Plain text for download
  //   dataIndex: 'kpi',
  //   key: 'kpi',
  //   width: 100,
  //   format: mathFormat,
  //   render: (item: any) => <div style={{ textAlign: 'right', width: `${'Monthly Gross Profit KPI'.length * 8}px`, textWrap: 'nowrap' }}>
  //     {mathFormat(item?.kpi) || '-'}
  //   </div>
  // }
]

export const OverallNowProfitList = (formatMessage: IntlShape['formatMessage']) => [
  {
    key: 'total',
    title: formatMessage({ id: 'dbs_monthly_overall_profit' }),
    content: 'YoY,MoM',
  },
  {
    key: 'yoy',
    title: 'YOY',
    content: '',
  },
  {
    key: 'mom',
    title: 'MOM',
    content: '',
  },
  // {
  //   key: 'kpi',
  //   title: formatMessage({ id: 'dbs_monthly_gross_profit_kpi' }),
  //   content: '',
  // }
];
export const RevenueNowProfitList = (formatMessage: IntlShape['formatMessage']) => [
  {
    key: 'total',
    title: formatMessage({ id: 'dbs_monthly_gross_revenue' }),
    content: 'YoY,MoM',
  },
  {
    key: 'yoy',
    title: 'YOY',
    content: '',
  },
  {
    key: 'mom',
    title: 'MOM',
    content: '',
  },
  {
    key: 'kpi',
    title: formatMessage({ id: 'dbs_monthly_gross_revenue_kpi' }),
    content: '',
  }
];

export const OverallIncomeColums = (formatMessage: IntlShape['formatMessage'], period?: string) => {
  const yearPrefix = getYearPrefix(period); // e.g. "25"
  const currentYear = period ? period.split('-')[0] : '';
  const priorYear = currentYear ? String(Number(currentYear) - 1) : '';
  const priorYearPrefix = priorYear ? priorYear.slice(-2) : '';

  return [
    {
      // header: <div style={{ fontWeight: 700, marginLeft: '11px' }}>{formatMessage({ id: 'dbs_dashboard' })}</div>, // Deleted dashboard header
      header: <div style={{ fontWeight: 700, marginLeft: '11px' }}></div>,
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (cvm: any) => <span style={{ fontWeight: 400, marginLeft: cvm.children ? '-11px' : '11px' }}> {localizeLabel(cvm?.category, formatMessage)}</span>
    },
    {
      header: (
        <DefinitionBubble
          definition="Prior year full-year actual"
          updateTime={period ? buildFullYearPeriod(`${priorYear}-12`) : ''}
          updateTimeLabel="Period:"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{priorYearPrefix}A</span>
            <span style={{ fontSize: '12px', fontWeight: 400, color: '#838697' }}>(M$)</span>
          </div>
        </DefinitionBubble>
      ),
      dataIndex: '24A',
      key: '24A', // previous year actual
      width: 70,
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'24A(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['24A'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition="Year-to-date (as of month end) actual"
          updateTime={buildPeriod(period)}
          updateTimeLabel="Period:"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{yearPrefix}A</span>
            <span style={{ fontSize: '12px', fontWeight: 400, color: '#838697' }}>(M$)</span>
          </div>
        </DefinitionBubble>
      ),
      dataIndex: '25A',
      key: '25A', // current year actual
      width: 70,
      // render: (cvm: any) => <div style={{ color: cvm?.['25A'] < 0 ? '#EF5858' : '', textAlign: 'right', width: `${'25A(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['25A'])}</div>
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'25A(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['25A'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition={`(${currentYear} year-to-date actual / ${priorYear} actual for the same period) - 1`}
          updateTime={buildPeriod(period)}
          updateTimeLabel="Period:"
        >
          {yearPrefix}A YoY%
        </DefinitionBubble>
      ),
      dataIndex: '25A_YoY_percent',
      key: '25A_YoY_percent',
      width: 70,
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'25A YoY%'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{formatPercentValue(cvm?.['25A_YoY_percent'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition="Year-to-date actual / year-to-date L1 KPI"
          updateTime={buildPeriod(period)}
          updateTimeLabel="Period:"
        >
          %YTM KPI
        </DefinitionBubble>
      ),
      dataIndex: 'YTM_KPI_percent',
      key: 'YTM_KPI_percent',
      width: 70,
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'%YTM KPI'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{formatPercentValue(cvm?.['YTM_KPI_percent'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition="Year-to-date actual / full-year L1 KPI"
          updateTime={buildPeriod(period)}
          updateTimeLabel="Period:"
        >
          %{yearPrefix} KPI
        </DefinitionBubble>
      ),
      dataIndex: '25_KPI_percent',
      key: '25_KPI_percent',
      width: 70,
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'%25 KPI'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{formatPercentValue(cvm?.['25_KPI_percent'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition="Full-year forecast"
          updateTime={buildFullYearPeriod(period)}
          updateTimeLabel="Period:"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{yearPrefix}F</span>
            <span style={{ fontSize: '12px', fontWeight: 400, color: '#838697' }}>(M$)</span>
          </div>
        </DefinitionBubble>
      ),
      dataIndex: '25F',
      key: '25F',
      width: 70,
      // render: (cvm: any) => <div style={{ color: cvm?.['25F'] < 0 ? '#EF5858' : '', textAlign: 'right', width: `${'25F(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['25F'])}</div>
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'25F(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['25F'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition="Full-year L1 KPI"
          updateTime={buildFullYearPeriod(period)}
          updateTimeLabel="Period:"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>{yearPrefix}B</span>
            <span style={{ fontSize: '12px', fontWeight: 400, color: '#838697' }}>(M$)</span>
          </div>
        </DefinitionBubble>
      ),
      dataIndex: '25B',
      key: '25B',
      width: 70,
      // render: (cvm: any) => <div style={{ color: cvm?.['25B'] < 0 ? '#EF5858' : '', textAlign: 'right', width: `${'25B(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['25B'])}</div>
      render: (cvm: any) => <div style={{ textAlign: 'right', width: `${'25B(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['25B'])}</div>
    },
    {
      header: (
        <DefinitionBubble
          definition="Full-year forecast minus full-year L1 KPI"
          updateTime={buildFullYearPeriod(period)}
          updateTimeLabel="Period:"
        >
          <div style={{ textAlign: 'center', width: `${'Surplus/Deficit(M$)'.length * 7 + 40}px`, textWrap: 'nowrap' }}>Surplus/Deficit</div>
        </DefinitionBubble>
      ),
      dataIndex: 'surplus_deficit',
      key: 'surplus_deficit',
      width: 140,
      render: (cvm: any) => <div style={{ color: cvm?.['surplus_deficit'] < 0 ? '#EF5858' : '', textAlign: 'right', width: `${'Surplus/Deficit(M$)'.length * 7 + 10}px`, textWrap: 'nowrap' }}>{toMillions(cvm?.['surplus_deficit'])}</div>
    },
  ];
};


export const IEGGFinancialsDashboardColumns = (
  formatMessage: IntlShape['formatMessage'],
  isAllExpanded?: boolean,
  period?: string,
  onToggleExpandAll?: () => void,
  isNarrowWith = false,
) => {
  // const currentYear = period ? period.split('-')[0] : '';
  // const yearPrefix = currentYear ? currentYear.slice(-2) : '25';

  // const isNarrowWith = false;

  const yearPrefix = getYearPrefix(period); // e.g. "25"
  const currentYear = period ? period.split('-')[0] : '';
  const priorYear = currentYear ? String(Number(currentYear) - 1) : '';
  // const priorYearPrefix = priorYear ? priorYear.slice(-2) : '';

  return [
    {
      header: (
        <div>
          {onToggleExpandAll ? (
            <ExpandCollapseButton
              isExpanded={!!isAllExpanded}
              onClick={() => onToggleExpandAll()}
              title={formatMessage({ id: 'dbs_project' })}
              className="dbs-overall-financials-expand-all"
            />
          ) : (
            <span style={{ fontWeight: 700 }}>
              {formatMessage({ id: 'dbs_project' })}
            </span>
          )}
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      width: 130,
      render: (cvm: any) => {
        return (
          <span style={{ display: 'inline-block', fontWeight: 400 }}>
            {localizeLabel(cvm?.name, formatMessage)}
          </span>
        );
      }
    },
    // ---- Gross Revenue 列 ----
    {
      // header: rendTitle({ title: `${yearPrefix}A`, titleSize: '14px', tipSize: '12px' }), 参考列
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            // definition="Prior year full-year actual"
            // updateTime={period ? buildFullYearPeriod(`${priorYear}-12`) : ''}
            // updateTimeLabel="Period:"
            definition="Year-to-date (as of month end) actual"
            updateTime={buildPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}A`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'actual_gross_revenue',
      key: 'actual_gross_revenue',
      width: isNarrowWith ? 40 : 60,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.actual_gross_revenue)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition={`(${currentYear} year-to-date actual / ${priorYear} actual for the same period) - 1`}
            updateTime={buildPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}A YoY%`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'actual_yoy_gross_revenue',
      key: 'actual_yoy_gross_revenue',
      width: isNarrowWith ? 60 : 80,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {formatPercentValue100(cvm?.actual_yoy_gross_revenue)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Full-year L2 KPI"
            updateTime={buildFullYearPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}B(L2)`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'budget_gross_revenue',
      key: 'budget_gross_revenue',
      width: isNarrowWith ? 40 : 60,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.budget_gross_revenue)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Year-to-date actual / full-year L2 KPI"
            updateTime={buildPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}A/${yearPrefix}B(L2)%`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'actual_vs_budget_gross_revenue',
      key: 'actual_vs_budget_gross_revenue',
      width: isNarrowWith ? 60 : 80,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {formatPercentValue100(cvm?.actual_vs_budget_gross_revenue)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Full-year forecast"
            updateTime={buildFullYearPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}F`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'forecast_gross_revenue',
      key: 'forecast_gross_revenue',
      width: isNarrowWith ? 40 : 60,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.forecast_gross_revenue)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Full-year forecast minus full-year L2 KPI"
            updateTime={buildFullYearPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}F VS ${yearPrefix}B(L2)`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'forecast_vs_budget_gross_revenue',
      key: 'forecast_vs_budget_gross_revenue',
      width: isNarrowWith ? 60 : 80,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {formatVSValue(cvm?.forecast_vs_budget_gross_revenue)}
        </div>
      )
    },
    // ---- Profit 列 ----
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Year-to-date (as of month end) actual"
            updateTime={buildPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}A`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'actual_profit',
      key: 'actual_profit',
      width: isNarrowWith ? 40 : 60,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.actual_profit)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Full-year L2 KPI"
            updateTime={buildFullYearPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}B(L2)`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'budget_profit',
      key: 'budget_profit',
      width: isNarrowWith ? 45 : 65,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.budget_profit)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Full-year forecast"
            updateTime={buildFullYearPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}F`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'forecast_profit',
      key: 'forecast_profit',
      width: isNarrowWith ? 40 : 60,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {toMillions(cvm?.forecast_profit)}
        </div>
      )
    },
    {
      header: (
        <div className="dbo-card-title dbs-card-title">
          <DefinitionBubble
            definition="Full-year forecast minus full-year L2 KPI"
            updateTime={buildFullYearPeriod(period)}
            updateTimeLabel="Period:"
          >
            <span className="dbo-card-titles" style={{ fontSize: '14px' }}>{`${yearPrefix}F VS ${yearPrefix}B(L2)`}</span>
          </DefinitionBubble>
        </div>
      ),
      dataIndex: 'forecast_vs_budget_profit',
      key: 'forecast_vs_budget_profit',
      width: isNarrowWith ? 60 : 80,
      align: 'right',
      render: (cvm: any) => (
        <div style={{ textAlign: 'right', width: '57px', textWrap: 'nowrap' }}>
          {formatVSValue(cvm?.forecast_vs_budget_profit)}
        </div>
      )
    },
  ];
};
